import React from 'react';
import ReactMarkdown from 'react-markdown';
import CustomTooltip from '../../../components/CustomTooltip/CustomTooltip';
import { withDefaultScheme } from '../../../utils/linkUrlUtils';

interface MarkdownLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: React.ReactNode;
}

export function MarkdownLink({
  children,
  href
}: Readonly<MarkdownLinkProps>): React.ReactElement {
  return (
    <CustomTooltip content={href ?? ''}>
      <a
        href={withDefaultScheme(href ?? '')}
        target='_blank'
        rel='noopener noreferrer nofollow'
        className='font-normal no-underline text-[#009389] hover:underline'
      >
        {children}
      </a>
    </CustomTooltip>
  );
}

interface MarkdownSxProps extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  isUser?: boolean;
}

function MarkdownParagraph({
  children,
  isUser
}: Readonly<MarkdownSxProps>): React.ReactElement {
  return (
    <p className={`text-sm ${isUser ? 'text-white' : 'text-brand-main'}`}>
      {children}
    </p>
  );
}

function MarkdownListItem({
  children,
  isUser
}: Readonly<MarkdownSxProps>): React.ReactElement {
  return (
    <li className={`text-sm ${isUser ? 'text-white' : 'text-brand-main'}`}>
      {children}
    </li>
  );
}

function generateMarkdownComponents(isUser: boolean): Record<string, React.FC> {
  function MarkdownParagraphWithProps({
    children,
    ...props
  }: Readonly<MarkdownSxProps>): React.ReactElement {
    return (
      <MarkdownParagraph {...props} isUser={isUser}>
        {children}
      </MarkdownParagraph>
    );
  }

  function MarkdownListItemWithProps({
    children,
    ...props
  }: Readonly<MarkdownSxProps>): React.ReactElement {
    return (
      <MarkdownListItem {...props} isUser={isUser}>
        {children}
      </MarkdownListItem>
    );
  }

  return {
    a: MarkdownLink,
    p: MarkdownParagraphWithProps,
    li: MarkdownListItemWithProps
  };
}

interface MarkdownMessageProps {
  children: string;
  isUser: boolean;
}

function MarkdownMessage({
  children,
  isUser
}: Readonly<MarkdownMessageProps>): React.ReactElement {
  const components = generateMarkdownComponents(isUser);
  return <ReactMarkdown components={components}>{children}</ReactMarkdown>;
}

export default MarkdownMessage;
